import React, { useEffect, useState, useRef } from 'react';
import { IconButton, CircularProgress } from '@material-ui/core';
import { PlayArrow as PlayArrowIcon, Pause as PauseIcon, BarChart as BarChartIcon } from '@material-ui/icons';
import SettingsButton from './SettingsButton';
import {JazeemInventoryEvent} from "src/API";
import useStore from "src/store";
import moment from 'moment';
import {REPLAY} from "src/globalVariables/globalVariables";
import KDEPlot from "src/components/KDEPlot";

// Declare types for component props
type ProgressBarProps = {
    startTime: string;
    endTime: string;
    reset: boolean;
    setReset: (r:boolean) => void;
};

const ProgressBar: React.FC<ProgressBarProps> = ({ startTime, endTime, reset, setReset }) => {
    // State definitions
    const [isPlaying, setIsPlaying] = useState(false);                  // Tracks play/pause state
    const [progress, setProgress] = useState(0);                        // Tracks playback progress in milliseconds
    const [isDragging, setIsDragging] = useState(false);                // Tracks whether user is dragging the progress bar
    const [isHovering, setIsHovering] = useState(false);                // Tracks whether mouse is hovering over progress bar
    const [playbackSpeed, setPlaybackSpeed] = useState(1);              // Tracks playback speed (1x, 1.5x, etc.)
    const [dataQueue, setDataQueue] = useState<any[]>([]);                // Data queue storing events
    const [isLoading, setIsLoading] = useState(false);                  // Tracks if data is loading
    const [lastFetchedTime, setLastFetchedTime] = useState<Date | null>(null); // Last time data was fetched
    const addInventoryEvent = useStore((state) => state.addInventoryEvent);
    const asinTiles = useStore((state) => state.asinTiles);
    const setTiles = useStore((state) => state.setTiles);
    const [isAtEnd, setIsAtEnd] = useState(false);                      // Checks if playback bar is at the end or not
    const [showDensityGraph, setShowDensityGraph] = useState(false);
    const [densityDataList, setDensityDataList] = useState<any[]>([]);
    const [isDensityLoading, setIsDensityLoading] = useState(false);
    const [divWidth, setDivWidth] = useState(0);

    // Refs
    const barRef = useRef<HTMLDivElement>(null);
    const playButtonRef = useRef(null);
    const densityGraphButtonRef = useRef(null);
    const divRef = useRef(null);

    // Time conversion to UTC
    const start = moment.utc(startTime).valueOf();
    const end = moment.utc(endTime).valueOf();
    const total = end - start;

    // Resets progress, dataQueue, and playback state
    useEffect(() => {
        if (reset) {
            setProgress(0);
            setDataQueue([]);
            setIsPlaying(false);
            setIsAtEnd(false);
            setTiles(asinTiles.filter(tile => tile.liveOrReplay !== REPLAY));
            setLastFetchedTime(new Date(start));
            setReset(false);
            setShowDensityGraph(false);
            setIsDensityLoading(false);
            setDensityDataList([]);
        }

        if(isAtEnd){
            setDataQueue([]);
            setIsPlaying(false);
            setLastFetchedTime(null);
        }
    }, [reset]);

    useEffect(() => {
        if (divRef.current) {
            // @ts-ignore
            setDivWidth(divRef.current.offsetWidth);
        }
    }, [divRef]);

    // Fetches new data if required
    useEffect(() => {
        const dataQueueLength = 100;
        const timeDifference = 60 * 60 * 1000;
        const fetchDataIfNeeded = async () => {
            if (dataQueue.length < dataQueueLength) {
                if(lastFetchedTime?.toISOString() == new Date(end).toISOString())
                    return;
                setIsLoading(true);
                const fetchStartTime = lastFetchedTime ? lastFetchedTime : new Date(start+progress);
                let fetchEndTime = new Date(fetchStartTime.getTime() + timeDifference);
                if(fetchEndTime.toISOString() > new Date(end).toISOString()){
                    fetchEndTime = new Date(end);
                }
                setLastFetchedTime(fetchEndTime);
                setIsLoading(false);
            }
        };
        if (isPlaying && !isAtEnd) {
            fetchDataIfNeeded().then(r => {});
        }
        else {
            setIsLoading(false);
        }
    }, [isPlaying, progress, lastFetchedTime, dataQueue.length]);


    // Toggles play/pause state
    const handlePlayClick = async () => {
        if(!isAtEnd)
            setIsPlaying(prev => !prev);
    };

    //Logic post-drag-release of the progress bar.
        const handleDragRelease = () => {
            // Empty the dataQueue
            setDataQueue([]);
            const currentTimestamp = new Date(start + progress);
            if (lastFetchedTime && currentTimestamp.toISOString() > lastFetchedTime.toISOString()) {
                setTiles(asinTiles.filter(tile => new Date(tile.dateAdded).toISOString() >= currentTimestamp.toISOString() && tile.liveOrReplay === REPLAY));
            }
            else{
            setTiles( asinTiles.filter(tile => {
                return !(new Date(tile.dateAdded).toISOString() > currentTimestamp.toISOString() && tile.liveOrReplay === REPLAY);
                }));
            }
    }

    // Mouse handlers for drag behavior
    useEffect(() => {
        setIsAtEnd(false);
        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                handleDragRelease();
            }
        };
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, [start, isDragging, progress]);


    // Handlers for dragging progress bar
    const handleMouseDown = () => setIsDragging(true);
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging || !barRef.current) return;
        const rect = barRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const newProgress = x * total / rect.width;

        setProgress(newProgress);
        setLastFetchedTime(new Date(start + newProgress));
    };

    // Handlers for mouse hover
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    // Handler for density graph button
    const handleDensityGraphToggle = async () => {
        // If the density graph is being shown, fetch the data
        if (densityDataList.length == 0) {
            setIsDensityLoading(true);
            // await fetchDensityData(new Date(start), new Date(end));
            setIsDensityLoading(false);
        }
        setShowDensityGraph(prevState => !prevState);
    }

    // Interval logic for progressing the playback bar
    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | null = null;
        if (isPlaying) {
            interval = setInterval(() => {
                setProgress(prevState => {
                    if (prevState < total) {
                        if(prevState + 1000 * playbackSpeed >= total){
                            setIsAtEnd(true);
                            return total;
                        }
                        return prevState + 1000 * playbackSpeed;
                    } else {
                        setIsAtEnd(true);
                        if (interval) clearInterval(interval);
                        return prevState;
                    }
                });
            }, 1000);
        } else if (!isPlaying && progress !== 0 && interval) {
            clearInterval(interval);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isPlaying, progress, total, playbackSpeed]);

    // Processes events from data queue based on current playback position and event type
    useEffect(() => {
        function isJazeemInventoryEvent(value: any): value is JazeemInventoryEvent {
            return 'process' in value && value.process == "checkout";
        }

        function processEvent(value: any) {
            if (isJazeemInventoryEvent(value))
                addInventoryEvent(value, REPLAY);
        }
        if (dataQueue.length > 0) {
            // Compute the start and end range based on progress and playbackSpeed
            const startRange = new Date(start + progress).toISOString();
            const endRange = new Date(start + progress + (playbackSpeed * 1000)).toISOString();

            // Filter events within the range
            const eventsToProcess = dataQueue.filter(event => {
                const eventTime = new Date(event.timestamp).toISOString();
                return eventTime >= startRange && eventTime < endRange;
            });

            // Process and remove those events
            for (const event of eventsToProcess) {
                processEvent(event);
                setDataQueue(prevDataQueue => {
                    return prevDataQueue.filter(e => e !== event);
                });
            }
        }
    }, [dataQueue, progress, playbackSpeed, start]);

    return (
        <div ref = {divRef} style={{ position: 'relative', padding: '30px 100px', height: '127px'}}>
            <div style={{display: 'flex', flexDirection: 'column', marginTop: '0' }}>
                {/* Conditional Render of KDE Plot */}
                {showDensityGraph && (
                    <div style={{ marginBottom: '0', marginTop: '0' }}>
                        <KDEPlot events={densityDataList} startTime={startTime} endTime={endTime} width={divWidth}/>
                    </div>
                )}
                {/* Progress Bar */}
                <div
                    ref={barRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    style={{ height: '10px', background: '#969696', marginTop: '0', position: 'relative' }}>
                    <div
                        data-testid="playback-bar"
                        style={{
                            height: '10px',
                            background: '#000000',
                            width: `${(progress / total) * 100}%`,
                        }}></div>
                    {isHovering && (
                        <div
                            data-testid = "hover-info"
                            style={{
                            position: 'absolute',
                            bottom: '20px',
                            left: `${(progress / total) * 100}%`,
                            transform: 'translateX(-50%)',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            backgroundColor: '#fff',
                            boxShadow: '0px 0px 10px rgba(0,0,0,0.2)',
                            whiteSpace: 'nowrap'
                        }}>
                            {new Date(start + progress).toISOString().replace('T', ' ').substr(0, 19)}
                        </div>
                    )}
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <IconButton data-testid = "play-button" color="inherit" onClick={handlePlayClick}>
                    {((isLoading && !isAtEnd)? <CircularProgress data-testid = "stream-circular-loading" size={24} /> : (isPlaying && !isAtEnd)? <PauseIcon /> : <PlayArrowIcon/>)}
                </IconButton>
                <SettingsButton playbackSpeed={playbackSpeed} setPlaybackSpeed={setPlaybackSpeed} />
                <IconButton data-testid = 'graph-button' color="inherit" onClick={handleDensityGraphToggle}>
                    {isDensityLoading ? <CircularProgress data-testid = "circular-loading" size={24} /> : <BarChartIcon />}
                </IconButton>
            </div>
        </div>
    );
}

export default ProgressBar;